import { forwardRef, useImperativeHandle, useRef } from 'react';
import CodeMirror, { EditorView, type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';

export type HtmlEditorHandle = { insert: (text: string) => void };

/**
 * Syntax-highlighted HTML source editor. Controlled via value/onChange so it
 * plugs straight into an antd <Form.Item>. Exposes `insert(text)` to drop a
 * snippet (e.g. a placeholder) at the current cursor position.
 */
export const HtmlEditor = forwardRef<HtmlEditorHandle, {
  value?: string;
  onChange?: (value: string) => void;
  height?: number;
}>(function HtmlEditor({ value = '', onChange, height = 560 }, ref) {
  const cmRef = useRef<ReactCodeMirrorRef>(null);

  useImperativeHandle(ref, () => ({
    insert(text: string) {
      const view = cmRef.current?.view;
      if (!view) return;
      const sel = view.state.selection.main;
      view.dispatch({
        changes: { from: sel.from, to: sel.to, insert: text },
        selection: { anchor: sel.from + text.length },
      });
      view.focus();
    },
  }));

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
      <CodeMirror
        ref={cmRef}
        value={value}
        height={`${height}px`}
        extensions={[html(), EditorView.lineWrapping]}
        onChange={(v) => onChange?.(v)}
        basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true, bracketMatching: true, autocompletion: true }}
      />
    </div>
  );
});
